import {Component, computed, EventEmitter, inject, Input, OnChanges, Output, signal} from '@angular/core';
import {DatePipe, NgClass} from '@angular/common';
import { EventDaySchedule, generateEventDaysRange, groupSessionsByEventDays, calculateNextAvailableStartTime, combineDateAndTimeToTimestamp } from './game-table-schedule.utils';
import { QuickSessionStepperComponent } from './quick-session-stepper/quick-session-stepper.component';
import {PermissionService} from '../../../../../core/services/permission.service';
import {AlertService} from '../../../../../shared/services/alert.service';
import {ConfirmDialogService} from '../../../../../shared/services/confirm-dialog.service';
import {Messages} from '../../../../../shared/constants/messages';
import {ApiResponse} from '../../../../../shared/model/api-response';
import {GameSession} from '../../../../../shared/model/game-session';
import {GameSessionService} from '../../../../../features/private/services/game-session.service';
import {
  CreateGameSessionPayload,
  GameSessionFormComponent
} from '../../../games/game-session-form/game-session-form.component';
import {GameTable} from '../../../../../shared/model/game-table';

@Component({
  selector: 'app-game-table-sessions',
  standalone: true,
  imports: [DatePipe, GameSessionFormComponent, NgClass, QuickSessionStepperComponent],
  templateUrl: './game-table-sessions.component.html',
  styleUrl: './game-table-sessions.component.css'
})
export class GameTableSessionsComponent implements OnChanges {
  private permissionService: PermissionService = inject(PermissionService);
  private alertService: AlertService = inject(AlertService);
  private confirmDialogService: ConfirmDialogService = inject(ConfirmDialogService);
  private gameSessionService: GameSessionService = inject(GameSessionService);

  @Input({required: true}) table!: GameTable;
  @Output() sessionsChanged = new EventEmitter<void>();

  protected gameSessions = signal<Array<GameSession>>([]);

  protected canCreateSession = signal<boolean>(false);

  protected eventDaysSchedule = computed<EventDaySchedule[]>(() => {
    const sessions = this.gameSessions();
    if (this.table?.event?.startDate) {
      const eventDays = generateEventDaysRange(this.table.event.startDate, this.table.event.endDate);
      const schedule = groupSessionsByEventDays(eventDays, sessions, this.canCreateSession());

      return schedule.map(day => {
          const dateLabel = day.date.toLocaleDateString('it-IT', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          });
          return { ...day, dateLabel };
      });
    }

    // Fallback per tavoli senza evento collegato
    const groups: Record<string, GameSession[]> = {};
    for (const session of sessions) {
      const dateKey = new Date(session.startTime).toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(session);
    }

    const schedule = Object.keys(groups).map(key => ({
      date: new Date(groups[key][0].startTime),
      dateLabel: key,
      sessions: groups[key]
    }));

    // Se l'utente può creare sessioni e non c'è una sessione per oggi, aggiungiamo "Oggi" per permettere la sessione rapida
    if (this.canCreateSession()) {
      const today = new Date();
      const todayKey = today.toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      if (!groups[todayKey]) {
        schedule.push({
          date: today,
          dateLabel: todayKey,
          sessions: []
        });
      }
    }

    // Sort by date to ensure proper ordering, especially if "Oggi" was appended
    return schedule.sort((a, b) => a.date.getTime() - b.date.getTime());
  });

  protected addingForDate = signal<Date | null>(null);
  protected quickStepperInitialTime = signal<string>('10:00');
  protected showOverride: boolean = false;
  protected requireOverride: boolean = false;
  protected showForm: boolean = false;
  protected isEditing: boolean = false;
  protected editingSession?: GameSession;
  private currentTableId?: number;

  get tableId(): number {
    return this.table?.id;
  }

  ngOnChanges(): void {
    this.computeSessionPermissions();

    const newTableId = this.tableId;
    if (newTableId && newTableId !== this.currentTableId) {
      this.currentTableId = newTableId;
      this.loadSessions(newTableId);
    }
  }

  onSessionSubmit(payload: CreateGameSessionPayload): void {
    if (this.isEditing && payload.session.id) {
      this.gameSessionService.updateSession(payload.session, payload.override).subscribe({
        next: (response: ApiResponse<GameSession>) => {
          if (response.success) {
            this.alertService.showSuccess('Sessione aggiornata con successo!');
            this.showForm = false;
            this.isEditing = false;
            this.loadSessions(this.tableId);
            this.sessionsChanged.emit();
          }
        },
        error: () => {
          this.alertService.showError(Messages.SESSION_SAVE_ERROR);
        }
      });
    } else {
      this.gameSessionService.createSession(payload.session, payload.override).subscribe({
        next: (response: ApiResponse<GameSession>) => {
          if (response.success) {
            this.alertService.showSuccess(Messages.SESSION_CREATED_SUCCESS);
            this.showForm = false;
            this.loadSessions(this.tableId);
            this.sessionsChanged.emit();
          }
        },
        error: () => {
          this.alertService.showError(Messages.SESSION_SAVE_ERROR);
        }
      });
    }
  }


  onQuickAddSession(date: Date, sessions: GameSession[]): void {
    this.addingForDate.set(date);
    const duration = this.table?.playedGame?.playingTime || 150;
    const initialTime = calculateNextAvailableStartTime(date, sessions, duration);
    this.quickStepperInitialTime.set(initialTime);
  }

  onQuickAddCancel(): void {
    this.addingForDate.set(null);
  }

  onQuickAddSessionConfirm(date: Date, timeStr: string): void {
    const startTime = combineDateAndTimeToTimestamp(date, timeStr);
    const duration = this.table?.playedGame?.playingTime || 150;
    const endTime = startTime + duration * 60000;

    const payload: Partial<GameSession> = {
      gameTableId: this.tableId,
      startTime,
      endTime
    };

    this.gameSessionService.createSession(payload).subscribe({
      next: (response: ApiResponse<GameSession>) => {
        if (response.success) {
          this.alertService.showSuccess(Messages.SESSION_CREATED_SUCCESS);
          this.addingForDate.set(null);
          this.loadSessions(this.tableId);
          this.sessionsChanged.emit();
        }
      },
      error: (err) => {
        if (!err?.error?.message) {
            this.alertService.showError(Messages.SESSION_SAVE_ERROR);
        }
      }
    });
  }

  onCancel(): void {
    this.resetForm();
  }

  onAddNewSession(): void {
    this.resetForm();
    this.showForm = true;
  }

  onCheckIn(sessionId: number): void {
    this.gameSessionService.checkIn(sessionId).subscribe({
      next: (response: ApiResponse<string>) => {
        if (response.success) {
          this.alertService.showSuccess('Check-in effettuato con successo!');
          this.loadSessions(this.tableId);
          this.sessionsChanged.emit();
        }
      },
      error: () => {
        this.alertService.showError('Errore durante il check-in');
      }
    });
  }

  onCheckOut(sessionId: number): void {
    this.gameSessionService.checkOut(sessionId).subscribe({
      next: (response: ApiResponse<string>) => {
        if (response.success) {
          this.alertService.showSuccess('Check-out effettuato con successo!');
          this.loadSessions(this.tableId);
          this.sessionsChanged.emit();
        }
      },
      error: () => {
        this.alertService.showError('Errore durante il check-out');
      }
    });
  }

  onEdit(id: number) {
    if (this.isEditing && this.editingSession?.id === id) {
      this.isEditing = false;
      this.showForm = false;
      return;
    }
    this.gameSessionService.getSessionById(id).subscribe({
      next: (response: ApiResponse<GameSession>) => {
        if (response.success && response.data) {
          this.editingSession = response.data;
          this.isEditing = true;
          this.showForm = true;
        }
      },
      error: () => {
        this.alertService.showError('Errore durante il recupero della sessione');
      }
    });
  }

  async onDelete(id: number) {
    const accepted = await this.confirmDialogService.confirm({
      title: Messages.CONFIRM_SESSION_DELETE_TITLE,
      message: Messages.CONFIRM_SESSION_DELETE,
      confirmLabel: Messages.CONFIRM_DELETE,
    });
    if (!accepted) {
      return;
    }
    this.gameSessionService.deleteSession(id, this.requireOverride).subscribe({
      next: (response: ApiResponse<string>) => {
        if (response.success) {
          this.alertService.showSuccess('Sessione eliminata con successo!');
          this.loadSessions(this.tableId);
          this.sessionsChanged.emit();
        }
      },
      error: () => {
        this.alertService.showError('Errore durante l\'eliminazione della sessione');
      }
    });
  }

  protected isCheckedIn(session: GameSession): boolean {
    // Check if the current user has a participation that is checked in and not checked out.
    return session.participation?.some(p => p.userId === this.currentUserId && p.checkedIn && !p.checkedOut) ?? false;
  }

  protected getEffectiveMaxPlayers(session: GameSession): number {
    return session.maxPlayers ?? this.table.maxPlayers;
  }

  protected getSessionPlayerCount(session: GameSession): number {
    return session.participation?.filter(p => p.checkedIn).length ?? 0;
  }

  protected isSessionFull(session: GameSession): boolean {
    const max = this.getEffectiveMaxPlayers(session);
    return max > 0 && this.getSessionPlayerCount(session) >= max;
  }

  protected isUserApproved(): boolean {
    const userId = this.currentUserId;
    if (!userId || !this.table?.participations) return false;
    const participation = this.table.participations.find(p => p.userProfile.userId === userId);
    return participation?.accepted ?? false;
  }

  protected canCheckIn(session: GameSession): boolean {
    // Can check in only if the user is approved at the table AND not currently checked in.
    return this.isUserApproved() && !this.isCheckedIn(session);
  }

  protected canCheckOut(session: GameSession): boolean {
    // Can check out only if the user is currently checked in.
    return this.isCheckedIn(session);
  }

  protected canManage(session: GameSession): boolean {
    return this.permissionService.canManageTable(this.table);
  }

  private get currentUserId(): number | undefined {
    return this.permissionService.getCurrentUser()?.id;
  }

  private loadSessions(tableId: number): void {
    this.gameSessionService.getSessionsByTable(tableId).subscribe({
      next: (response: ApiResponse<Array<GameSession>>) => {
        this.gameSessions.set(response.success && response.data ? response.data : []);
      },
      error: () => {
        this.gameSessions.set([]);
      }
    });
  }

  private computeSessionPermissions(): void {
    this.canCreateSession.set(this.permissionService.canManageTable(this.table));
    this.showOverride = false;
    this.requireOverride = false;
  }

  private resetForm(): void {
    this.showForm = false;
    this.isEditing = false;
    this.editingSession = undefined;
  }
}
